import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { useAuth } from "./AuthContext";
import {
    buildClarityPageId,
    getClarityRouteMetadata,
    identifyClarityUser,
    initClarity,
    setClarityTags,
} from "../utils/clarity";
import { initGoogleAnalytics, trackPageView } from "../utils/google_analytics";

const DEFAULT_ROUTE_METADATA = {
    appArea: "public",
    routeName: "unknown",
    featureSurface: "unknown",
    clarityEnabled: false,
};

const AnalyticsBridge = () => {
    const location = useLocation();
    const { isAuthenticated, loading, user } = useAuth();

    const routeMetadata = useMemo(
        () => getClarityRouteMetadata(location.pathname) || DEFAULT_ROUTE_METADATA,
        [location.pathname]
    );

    useEffect(() => {
        initGoogleAnalytics();
    }, []);

    useEffect(() => {
        if (!routeMetadata.clarityEnabled) return;
        initClarity();
    }, [routeMetadata.clarityEnabled]);

    useEffect(() => {
        trackPageView(location.pathname + location.search);

        if (!routeMetadata.clarityEnabled) return;

        setClarityTags({
            app_area: routeMetadata.appArea,
            route_name: routeMetadata.routeName,
            feature_surface: routeMetadata.featureSurface,
        });
    }, [
        location.pathname,
        location.search,
        routeMetadata.appArea,
        routeMetadata.clarityEnabled,
        routeMetadata.featureSurface,
        routeMetadata.routeName,
    ]);

    useEffect(() => {
        if (loading || !routeMetadata.clarityEnabled) return;

        setClarityTags({
            auth_state: isAuthenticated ? "authenticated" : "anonymous",
            has_known_user: isAuthenticated && user?.id ? "true" : "false",
        });

        if (isAuthenticated && user?.id) {
            identifyClarityUser({
                userId: user.id,
                pageId: buildClarityPageId(location.pathname),
            });
        }
    }, [
        isAuthenticated,
        loading,
        location.pathname,
        routeMetadata.clarityEnabled,
        user?.id,
    ]);

    return null;
};

export default AnalyticsBridge;
